/**
 * Admin, Kategorie a předměty (`/admin/kategorie`, WP11).
 * Kategorie = volné spravovatelné poličky (strom max 2 úrovně).
 * Předmět = číselník s ikonou a barvou křídy (hlavní štítek materiálu).
 * UX dle mockupu docs/design/mockups/06-admin-kategorie.html.
 */

import { Fragment, useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  CornerDownRight,
  EyeOff,
  Info,
  Pencil,
  Plus,
  Save,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import type { ApiError, ChalkColor } from '../../lib/api';
import {
  adminApi,
  type AdminCategory,
  type AdminSubject,
  type CategoryCreate,
} from '../../lib/admin';
import {
  CHALK_OPTIONS,
  CHALK_SWATCH,
  ICON_OPTIONS,
  iconForCode,
  subjectIcon,
} from '../../lib/format';

/* Pomocníci */

function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const bySort = (a: { sort_order: number; name: string }, b: { sort_order: number; name: string }) =>
  a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'cs');

function errMessage(err: unknown): string | undefined {
  return (err as ApiError)?.message;
}

/* Editor kategorie */

type CatDraft = {
  id: number | null;
  name: string;
  slug: string;
  slugTouched: boolean;
  parent_id: number | null;
  icon: string;
  description: string;
};

function emptyDraft(parentId: number | null = null): CatDraft {
  return {
    id: null,
    name: '',
    slug: '',
    slugTouched: false,
    parent_id: parentId,
    icon: 'grad',
    description: '',
  };
}

function draftFrom(cat: AdminCategory): CatDraft {
  return {
    id: cat.id,
    name: cat.name,
    slug: cat.slug,
    slugTouched: true,
    parent_id: cat.parent_id,
    icon: cat.icon,
    description: cat.description ?? '',
  };
}

const inputClass =
  'mt-1 h-11 w-full rounded-xl border border-line bg-paper px-3 text-ink outline-none focus:border-board';

function IconPicker({ value, onChange }: { value: string; onChange: (code: string) => void }) {
  return (
    <div className="mt-1.5 grid grid-cols-5 gap-2 sm:grid-cols-7">
      {ICON_OPTIONS.map(({ code, label }) => {
        const Icon = iconForCode(code);
        const active = code === value;
        return (
          <button
            key={code}
            type="button"
            onClick={() => onChange(code)}
            aria-label={label}
            title={label}
            className={`grid h-11 w-full place-items-center rounded-xl border transition ${
              active
                ? 'border-board bg-board text-chalk'
                : 'border-line bg-paper text-board hover:border-board'
            }`}
          >
            <Icon className="h-5 w-5" strokeWidth={2} />
          </button>
        );
      })}
    </div>
  );
}

function CategoryEditor({
  draft,
  setDraft,
  categories,
  onSaved,
  onDeleted,
}: {
  draft: CatDraft;
  setDraft: (d: CatDraft) => void;
  categories: AdminCategory[];
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isNew = draft.id === null;
  const existing = isNew ? null : (categories.find((c) => c.id === draft.id) ?? null);
  const Badge = subjectIcon(draft.icon);

  // Rodičem může být jen kořen (a ne kategorie sama).
  const parentOptions = categories
    .filter((c) => c.parent_id === null && c.id !== draft.id)
    .sort(bySort);

  const setName = (name: string) => {
    setDraft({
      ...draft,
      name,
      // slug se generuje z názvu, dokud ho uživatel ručně nezměnil (jen u nové kategorie).
      slug: !draft.slugTouched && isNew ? slugify(name) : draft.slug,
    });
  };

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error('Zadejte název kategorie');
      return;
    }
    setSaving(true);
    try {
      const body: CategoryCreate = {
        name: draft.name.trim(),
        slug: draft.slug.trim() || slugify(draft.name),
        parent_id: draft.parent_id,
        icon: draft.icon,
        description: draft.description.trim() || null,
      };
      if (isNew) {
        await adminApi.createCategory(body);
        toast.success('Kategorie vytvořena');
      } else {
        await adminApi.updateCategory(draft.id as number, body);
        toast.success('Kategorie uložena');
      }
      onSaved();
    } catch (err) {
      toast.error('Uložení selhalo', { description: errMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await adminApi.deleteCategory(draft.id as number);
      toast.success('Kategorie smazána');
      setConfirmDelete(false);
      onDeleted();
    } catch (err) {
      setConfirmDelete(false);
      toast.error('Nelze smazat', { description: errMessage(err) });
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-card border border-line bg-card p-4 shadow-card sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-serif text-xl">{isNew ? 'Nová kategorie' : 'Upravit kategorii'}</h2>
          {!isNew && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-paper2 px-2.5 py-1 text-xs font-semibold text-ink-soft">
              <Badge className="h-3.5 w-3.5" strokeWidth={2} /> {existing?.name ?? draft.name}
            </span>
          )}
        </div>

        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-sm font-semibold">Název</span>
            <input
              className={inputClass}
              value={draft.name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          <label className="block">
            <span className="text-sm font-semibold">Webová adresa (slug)</span>
            <div className="mt-1 flex items-center rounded-xl border border-line bg-paper focus-within:border-board">
              <span className="pl-3 text-sm text-ink-soft">/kategorie/</span>
              <input
                className="h-11 w-full rounded-xl bg-transparent px-1 text-ink outline-none"
                value={draft.slug}
                onChange={(e) =>
                  setDraft({ ...draft, slug: slugify(e.target.value), slugTouched: true })
                }
              />
            </div>
            <span className="mt-0.5 block text-xs text-ink-soft">
              Vytváří se sama z názvu.{' '}
              {!isNew && 'Změna u existující kategorie změní odkaz pro zákazníky.'}
            </span>
          </label>

          <label className="block">
            <span className="text-sm font-semibold">Nadřazená kategorie</span>
            <select
              className={inputClass}
              value={draft.parent_id ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, parent_id: e.target.value ? Number(e.target.value) : null })
              }
            >
              <option value="">žádná (hlavní kategorie)</option>
              {parentOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <span className="mt-0.5 block text-xs text-ink-soft">
              Nejvýše dvě úrovně: hlavní kategorie a její podkategorie.
            </span>
          </label>

          <div>
            <span className="text-sm font-semibold">Ikona</span>
            <IconPicker value={draft.icon} onChange={(icon) => setDraft({ ...draft, icon })} />
          </div>

          <label className="block">
            <span className="text-sm font-semibold">
              Popis <span className="font-normal text-ink-soft">(nepovinné)</span>
            </span>
            <textarea
              className="mt-1 min-h-[70px] w-full rounded-xl border border-line bg-paper px-3 py-2 text-sm text-ink outline-none focus:border-board"
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
          </label>

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving}
              className="inline-flex h-11 items-center gap-2 rounded-xl bg-board px-4 font-semibold text-chalk transition hover:bg-board-deep disabled:opacity-50"
            >
              <Save className="h-4 w-4" strokeWidth={2} /> Uložit
            </button>
            {!isNew && (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="inline-flex h-11 items-center gap-2 rounded-xl border border-line bg-card px-4 font-semibold text-error transition hover:bg-error/5"
              >
                <Trash2 className="h-4 w-4" strokeWidth={2} /> Smazat kategorii
              </button>
            )}
          </div>
        </div>
      </div>

      {confirmDelete && existing && (
        <div className="rounded-card border border-warning/40 bg-card p-4 shadow-card sm:p-5">
          <h3 className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="h-4 w-4 text-warning" strokeWidth={2} /> Smazat kategorii „
            {existing.name}"?
          </h3>
          <p className="mt-1.5 text-sm text-ink-soft">
            Kategorie obsahuje{' '}
            <strong className="text-ink">{existing.product_count} materiálů</strong>. Materiály se{' '}
            <strong className="text-ink">nesmažou</strong>, jen ztratí toto zařazení a zmizí
            příslušný chip z katalogu. Podkategorie je nutné smazat nebo přesunout předem.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => void doDelete()}
              className="inline-flex h-11 items-center gap-2 rounded-xl bg-error px-4 font-semibold text-white transition hover:brightness-95"
            >
              <Trash2 className="h-4 w-4" strokeWidth={2} /> Smazat
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="inline-flex h-11 items-center rounded-xl border border-line bg-card px-4 font-semibold text-ink-soft transition hover:border-board"
            >
              Zrušit
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* Řádek kategorie ve stromu */

function CategoryRow({
  cat,
  child,
  active,
  canUp,
  canDown,
  onSelect,
  onAddChild,
  onMove,
}: {
  cat: AdminCategory;
  child: boolean;
  active: boolean;
  canUp: boolean;
  canDown: boolean;
  onSelect: () => void;
  onAddChild?: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  const Icon = subjectIcon(cat.icon);
  const invisible = cat.product_count === 0;

  return (
    <li
      className={`flex min-h-[52px] items-center gap-2.5 rounded-xl border px-3 py-2 ${
        child ? 'ml-6 min-h-[48px] bg-card' : 'bg-paper'
      } ${active ? 'border-2 border-board' : 'border-line'}`}
    >
      <span
        className={`grid flex-none place-items-center rounded-[10px] ${
          child ? 'h-8 w-8 bg-paper2 text-board' : 'h-9 w-9 bg-board text-chalk'
        }`}
      >
        <Icon className={child ? 'h-4 w-4' : 'h-[18px] w-[18px]'} strokeWidth={2} />
      </span>
      <span className="min-w-0 grow">
        <span className="flex items-center gap-2 truncate font-semibold">
          {cat.name}
          {invisible && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-semibold text-ink"
              title="Zákazníci ji zatím nevidí"
            >
              <EyeOff className="h-3 w-3" strokeWidth={2} /> skrytá
            </span>
          )}
        </span>
        <span className="block truncate text-xs text-ink-soft">
          /kategorie/{cat.slug} · {cat.product_count} materiálů
        </span>
      </span>
      <span className="hidden gap-1 sm:flex">
        <button
          type="button"
          disabled={!canUp}
          onClick={() => onMove(-1)}
          className="grid h-11 w-9 place-items-center rounded-lg text-ink-soft transition hover:bg-paper2 disabled:opacity-30"
          aria-label="Nahoru"
        >
          <ChevronUp className="h-4 w-4" strokeWidth={2} />
        </button>
        <button
          type="button"
          disabled={!canDown}
          onClick={() => onMove(1)}
          className="grid h-11 w-9 place-items-center rounded-lg text-ink-soft transition hover:bg-paper2 disabled:opacity-30"
          aria-label="Dolů"
        >
          <ChevronDown className="h-4 w-4" strokeWidth={2} />
        </button>
      </span>
      {onAddChild && (
        <button
          type="button"
          onClick={onAddChild}
          className="grid h-11 w-11 place-items-center rounded-lg text-ink-soft transition hover:bg-paper2"
          aria-label="Přidat podkategorii"
        >
          <CornerDownRight className="h-4 w-4" strokeWidth={2} />
        </button>
      )}
      <button
        type="button"
        onClick={onSelect}
        className="grid h-11 w-11 place-items-center rounded-lg text-board transition hover:bg-paper2"
        aria-label="Upravit"
      >
        <Pencil className="h-4 w-4" strokeWidth={2} />
      </button>
    </li>
  );
}

/* Předměty */

function SubjectsCard({
  subjects,
  onChanged,
}: {
  subjects: AdminSubject[];
  onChanged: () => void;
}) {
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editIcon, setEditIcon] = useState('grad');
  const [editColor, setEditColor] = useState<ChalkColor>('white');

  const [newName, setNewName] = useState('');
  const [newIcon, setNewIcon] = useState('grad');
  const [newColor, setNewColor] = useState<ChalkColor>('white');

  const sorted = [...subjects].sort(bySort);

  const startEdit = (s: AdminSubject) => {
    setEditId(s.id);
    setEditName(s.name);
    setEditIcon(s.icon);
    setEditColor(s.color);
  };

  const saveEdit = async () => {
    if (!editName.trim() || editId === null) return;
    try {
      await adminApi.updateSubject(editId, {
        name: editName.trim(),
        icon: editIcon,
        color: editColor,
      });
      toast.success('Předmět uložen');
      setEditId(null);
      onChanged();
    } catch (err) {
      toast.error('Uložení selhalo', { description: errMessage(err) });
    }
  };

  const add = async () => {
    if (!newName.trim()) return;
    try {
      await adminApi.createSubject({ name: newName.trim(), icon: newIcon, color: newColor });
      toast.success('Předmět přidán');
      setNewName('');
      setNewIcon('grad');
      setNewColor('white');
      onChanged();
    } catch (err) {
      toast.error('Nepodařilo se přidat', { description: errMessage(err) });
    }
  };

  const remove = async (s: AdminSubject) => {
    try {
      await adminApi.deleteSubject(s.id);
      toast.success('Předmět smazán');
      onChanged();
    } catch (err) {
      toast.error('Nelze smazat', { description: errMessage(err) });
    }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= sorted.length) return;
    const ids = sorted.map((s) => s.id);
    const a = ids[index];
    const b = ids[target];
    if (a === undefined || b === undefined) return;
    ids[index] = b;
    ids[target] = a;
    try {
      await adminApi.reorderSubjects(ids);
      onChanged();
    } catch (err) {
      toast.error('Přeřazení selhalo', { description: errMessage(err) });
    }
  };

  return (
    <div className="rounded-card border border-line bg-card p-4 shadow-card sm:p-5">
      <h2 className="font-serif text-xl">Předměty</h2>
      <p className="mt-1 text-xs text-ink-soft">
        Hlavní štítek materiálu. Určuje ikonu a barvu křídy na náhledu. Předmět nejde smazat, dokud
        ho používá materiál, a aspoň jeden musí zůstat.
      </p>

      <ul className="mt-3 space-y-1.5">
        {sorted.map((s, i) => {
          const Icon = subjectIcon(s.icon);
          const used = s.product_count > 0;
          const last = sorted.length <= 1;
          if (editId === s.id) {
            return (
              <li
                key={s.id}
                className="flex flex-wrap items-center gap-2 rounded-xl border border-board bg-paper px-3 py-2"
              >
                <input
                  className="h-10 min-w-[9rem] grow rounded-xl border border-line bg-card px-3 text-sm outline-none focus:border-board"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
                <IconMiniPicker value={editIcon} onChange={setEditIcon} />
                <ColorPicker value={editColor} onChange={setEditColor} />
                <button
                  type="button"
                  onClick={() => void saveEdit()}
                  className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-board px-3 text-sm font-semibold text-chalk hover:bg-board-deep"
                >
                  <Save className="h-4 w-4" strokeWidth={2} /> Uložit
                </button>
                <button
                  type="button"
                  onClick={() => setEditId(null)}
                  className="inline-flex h-10 items-center rounded-xl border border-line px-3 text-sm font-semibold text-ink-soft hover:border-board"
                >
                  Zrušit
                </button>
              </li>
            );
          }
          return (
            <li
              key={s.id}
              className="flex min-h-[48px] items-center gap-2.5 rounded-xl border border-line bg-paper px-3 py-1.5"
            >
              <span
                className={`h-3 w-3 flex-none rounded-full ring-2 ring-line ${CHALK_SWATCH[s.color]}`}
                title={`křída: ${s.color}`}
              />
              <Icon className="h-4 w-4 flex-none text-board" strokeWidth={2} />
              <span className="grow font-semibold">
                {s.name}
                <span className="ml-1.5 text-xs font-normal text-ink-soft">
                  {s.product_count} materiálů
                </span>
              </span>
              <button
                type="button"
                onClick={() => void move(i, -1)}
                disabled={i === 0}
                className="grid h-11 w-8 place-items-center rounded-lg text-ink-soft transition hover:bg-paper2 disabled:opacity-30"
                aria-label="Nahoru"
              >
                <ChevronUp className="h-4 w-4" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={() => void move(i, 1)}
                disabled={i === sorted.length - 1}
                className="grid h-11 w-8 place-items-center rounded-lg text-ink-soft transition hover:bg-paper2 disabled:opacity-30"
                aria-label="Dolů"
              >
                <ChevronDown className="h-4 w-4" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={() => startEdit(s)}
                className="grid h-11 w-11 place-items-center rounded-lg text-board transition hover:bg-paper2"
                aria-label="Upravit"
              >
                <Pencil className="h-4 w-4" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={() => void remove(s)}
                disabled={used || last}
                title={
                  used
                    ? `Používá ${s.product_count} materiálů`
                    : last
                      ? 'Musí zůstat aspoň jeden předmět'
                      : 'Smazat'
                }
                className={`grid h-11 w-11 place-items-center rounded-lg transition ${
                  used || last ? 'text-line' : 'text-error hover:bg-error/5'
                }`}
                aria-label="Smazat"
              >
                <Trash2 className="h-4 w-4" strokeWidth={2} />
              </button>
            </li>
          );
        })}
      </ul>

      {/* inline přidání */}
      <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-line bg-paper2/60 p-3">
        <input
          className="h-11 min-w-[10rem] grow rounded-xl border border-line bg-paper px-3 text-sm outline-none focus:border-board"
          placeholder="Nový předmět (např. Biologie)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void add();
          }}
        />
        <IconMiniPicker value={newIcon} onChange={setNewIcon} />
        <ColorPicker value={newColor} onChange={setNewColor} />
        <button
          type="button"
          onClick={() => void add()}
          className="inline-flex h-11 items-center gap-2 rounded-xl bg-board px-4 text-sm font-semibold text-chalk transition hover:bg-board-deep"
        >
          <Plus className="h-4 w-4" strokeWidth={2} /> Přidat
        </button>
      </div>
    </div>
  );
}

function ColorPicker({
  value,
  onChange,
}: {
  value: ChalkColor;
  onChange: (c: ChalkColor) => void;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {CHALK_OPTIONS.map(({ code, label }) => (
        <button
          key={code}
          type="button"
          onClick={() => onChange(code)}
          aria-label={`${label} křída`}
          title={`${label} křída`}
          className={`h-7 w-7 rounded-full ${CHALK_SWATCH[code]} ${
            value === code ? 'ring-2 ring-board' : 'ring-1 ring-line'
          }`}
        />
      ))}
    </span>
  );
}

/** Kompaktní ikonový picker (dropdown) pro řádek předmětu. */
function IconMiniPicker({ value, onChange }: { value: string; onChange: (code: string) => void }) {
  const Icon = iconForCode(value);
  return (
    <label className="relative inline-grid h-11 w-11 place-items-center rounded-xl border border-line bg-card text-board">
      <Icon className="h-4 w-4" strokeWidth={2} />
      <select
        className="absolute inset-0 cursor-pointer opacity-0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Ikona předmětu"
      >
        {ICON_OPTIONS.map(({ code, label }) => (
          <option key={code} value={code}>
            {label}
          </option>
        ))}
      </select>
    </label>
  );
}

/* Stránka */

export default function AdminCategories() {
  const [categories, setCategories] = useState<AdminCategory[]>([]);
  const [subjects, setSubjects] = useState<AdminSubject[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<CatDraft | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cats, subs] = await Promise.all([adminApi.listCategories(), adminApi.listSubjects()]);
      setCategories(cats);
      setSubjects(subs);
    } catch (err) {
      toast.error('Nepodařilo se načíst', { description: errMessage(err) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const roots = categories.filter((c) => c.parent_id === null).sort(bySort);

  const move = async (cat: AdminCategory, dir: -1 | 1) => {
    const siblings = categories.filter((c) => c.parent_id === cat.parent_id).sort(bySort);
    const index = siblings.findIndex((c) => c.id === cat.id);
    const target = index + dir;
    if (target < 0 || target >= siblings.length) return;
    const ids = siblings.map((c) => c.id);
    const a = ids[index];
    const b = ids[target];
    if (a === undefined || b === undefined) return;
    ids[index] = b;
    ids[target] = a;
    try {
      await adminApi.reorderCategories(cat.parent_id, ids);
      await load();
    } catch (err) {
      toast.error('Přeřazení selhalo', { description: errMessage(err) });
    }
  };

  const afterCatChange = async () => {
    setDraft(null);
    await load();
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <h1 className="font-serif text-2xl">Kategorie a předměty</h1>
      <p className="mt-1 max-w-2xl text-sm text-ink-soft">
        <strong className="text-ink">Kategorie</strong> jsou poličky ve výkladu, materiál smí být ve
        více. <strong className="text-ink">Předmět</strong> je štítek na deskách, každý materiál má
        právě jeden a určuje ikonu a barvu náhledu.
      </p>

      <div className="mt-5 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
        {/* Levý sloupec: strom kategorií + předměty */}
        <section className="space-y-4">
          <div className="rounded-card border border-line bg-card p-4 shadow-card sm:p-5">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h2 className="font-serif text-xl">Kategorie výkladu</h2>
              <button
                type="button"
                onClick={() => setDraft(emptyDraft())}
                className="inline-flex h-10 items-center gap-2 rounded-xl bg-board px-3.5 text-sm font-semibold text-chalk transition hover:bg-board-deep"
              >
                <Plus className="h-4 w-4" strokeWidth={2} /> Přidat kategorii
              </button>
            </div>

            {loading ? (
              <div className="h-40 rounded-card bg-paper2 motion-safe:animate-pulse" />
            ) : roots.length === 0 ? (
              <p className="rounded-xl border border-dashed border-line bg-paper2/60 px-3 py-6 text-center text-sm text-ink-soft">
                Zatím žádné kategorie. Založte první tlačítkem „Přidat kategorii".
              </p>
            ) : (
              <ul className="space-y-1.5">
                {roots.map((root, ri) => {
                  const children = categories.filter((c) => c.parent_id === root.id).sort(bySort);
                  return (
                    <Fragment key={root.id}>
                      <CategoryRow
                        cat={root}
                        child={false}
                        active={draft?.id === root.id}
                        canUp={ri > 0}
                        canDown={ri < roots.length - 1}
                        onSelect={() => setDraft(draftFrom(root))}
                        onAddChild={() => setDraft(emptyDraft(root.id))}
                        onMove={(dir) => void move(root, dir)}
                      />
                      {children.map((kid, ki) => (
                        <CategoryRow
                          key={kid.id}
                          cat={kid}
                          child
                          active={draft?.id === kid.id}
                          canUp={ki > 0}
                          canDown={ki < children.length - 1}
                          onSelect={() => setDraft(draftFrom(kid))}
                          onMove={(dir) => void move(kid, dir)}
                        />
                      ))}
                    </Fragment>
                  );
                })}
              </ul>
            )}

            <p className="mt-3 flex items-start gap-2 rounded-xl bg-paper2 px-3 py-2.5 text-xs text-ink-soft">
              <Info className="mt-0.5 h-3.5 w-3.5 flex-none" strokeWidth={2} />
              Kategorie se zákazníkům ukáže, jakmile v ní je aspoň jeden zveřejněný materiál.
              Smazáním kategorie se materiály nesmažou.
            </p>
          </div>

          <SubjectsCard subjects={subjects} onChanged={() => void load()} />
        </section>

        {/* Pravý sloupec: editor kategorie */}
        <section className="space-y-4">
          {draft ? (
            <CategoryEditor
              draft={draft}
              setDraft={setDraft}
              categories={categories}
              onSaved={() => void afterCatChange()}
              onDeleted={() => void afterCatChange()}
            />
          ) : (
            <div className="rounded-card border border-dashed border-line bg-card/60 p-6 text-center text-sm text-ink-soft">
              Vyberte kategorii k úpravě, nebo přidejte novou.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
